/* eslint-disable import/no-unused-modules */
import styled from "styled-components";

import { Carousel } from "../Carousel";

import { Company } from "@otta/search/schema";
import { pxToRem } from "@otta/design-tokens";
import { media } from "@otta/design";

export const MobileOnly = styled.div`
  ${media({ from: "tablet" }, "screen")`
    display: none;
  `}
`;

const DesktopOnly = styled.div`
  ${media({ to: "tablet" }, "screen")`
    display: none;
  `}
`;

const ImageGrid = styled.div<{ totalImages: number }>`
  ${media({ from: "tablet" })`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    gap: ${pxToRem(24)};
    grid-auto-rows: auto;

    ${({ totalImages }) => {
      return totalImages === 1
        ? `grid-template-columns: 1fr;`
        : totalImages === 2 || totalImages === 4
        ? `grid-template-columns: 1fr 1fr;`
        : totalImages === 3
        ? `grid-template-columns: 1fr 1fr 1fr;`
        : `grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;`;
    }}
  `}
`;

const ImageWrapper = styled.div<{ idx: number; totalImages: number }>`
  overflow: hidden;

  ${media({ from: "tablet" })`
    ${({ idx, totalImages }) => {
      if (totalImages === 4 && (idx === 2 || idx === 3)) {
        return `grid-row-start: 2;`;
      }

      if (totalImages === 5) {
        let styles = idx <= 2 ? `grid-row-start: 1;` : `grid-row-start: 2;`;

        styles +=
          idx === 0
            ? `grid-column-start: 1; grid-column-end: 3;`
            : idx === 1
            ? `grid-column-start: 3; grid-column-end: 5;`
            : idx === 2
            ? `grid-column-start: 5; grid-column-end: 7;`
            : idx === 3
            ? `grid-column-start: 1; grid-column-end: 4;`
            : `grid-column-start: 4; grid-column-end: 7;`;

        return styles;
      }
    }}
  `}
`;

const MobileImageWrapper = styled.div`
  min-width: 100vw;
  margin: 2% 0;
  height: 100%;
  scroll-snap-align: center;
`;

const InnerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const OfficePhotos = ({ company }: { company: Company.Company }) => {
  const { officePhotos } = company;

  if (!officePhotos.length) {
    return null;
  }

  const mobileCards = officePhotos.map((photo, idx) => (
    <MobileImageWrapper key={idx}>
      <InnerImg
        src={photo.source}
        srcSet={photo.small ?? undefined}
        alt={photo.alt || ""}
      />
    </MobileImageWrapper>
  ));

  return (
    <div data-testid="office-photos">
      <MobileOnly>
        <Carousel cards={mobileCards} displayDots />
      </MobileOnly>
      <DesktopOnly>
        <ImageGrid totalImages={officePhotos.length}>
          {officePhotos.map((photo, idx) => (
            <ImageWrapper
              data-testid={`company-photo-${idx + 1}`}
              key={idx}
              totalImages={officePhotos.length}
              idx={idx}
            >
              {officePhotos.length === 1 && (
                <InnerImg
                  src={photo.source}
                  srcSet={photo.large ?? undefined}
                  alt={photo.alt || ""}
                />
              )}
              {(officePhotos.length === 2 || officePhotos.length >= 4) && (
                <InnerImg
                  src={photo.source}
                  srcSet={photo.medium ?? undefined}
                  alt={photo.alt || ""}
                />
              )}
              {officePhotos.length === 3 && (
                <InnerImg
                  src={photo.source}
                  srcSet={photo.small ?? undefined}
                  alt={photo.alt || ""}
                />
              )}
            </ImageWrapper>
          ))}
        </ImageGrid>
      </DesktopOnly>
    </div>
  );
};
